<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataTypeToShow"
          :data-type="card.dataType"
        />
      </v-row>

      <v-row
        no-gutters
        class="filter-sac-status-modal mt-5 mx-5"
      >
        <span class="filter-sac-status-modal__description">{{ $t('components.filter.ModalFilters.label.sacStatus.descriptionPart1') }}<span class="filter-sac-status-modal__contrast">{{ $t('components.filter.ModalFilters.label.sacStatus.statusTransitionOld') }}</span>{{ $t('components.filter.ModalFilters.label.sacStatus.descriptionPart2') }}<span class="filter-sac-status-modal__contrast">{{ $t('components.filter.ModalFilters.label.sacStatus.statusTransitionNew') }}</span>{{ $t('components.filter.ModalFilters.label.sacStatus.descriptionPart3') }}</span>
      </v-row>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <PageFilter
            v-model="pagesSelected"
            :pages-items="pagesFormatted"
            :clearable="displayClearable('pages')"
            @clearInfo="trackEvent"
            @input="changeFilterValue()"
          />
        </v-col>

        <v-col
          v-for="(filter, index) in filtersObj"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            :channels="channels"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import PageFilter from './PageFilter';
import StatusFilter from './StatusFilter';
import SacTypeFilter from './SacTypeFilter';
import RepliersFilter from './RepliersFilter';
import sacFilterModel from '@/mixins/sac-filter-model';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { isEqual } from 'lodash';

import { Flags } from '@/plugins/rollout';
import { mapGetters } from 'vuex';
import {
  DATA_TYPE,
  FILTER_NAMES,
} from '@/store/modules/cards/constants';

export default {
  name: 'FilterSacStatusModal',
  components: {
    StIcon,
    PageFilter,
    StatusFilter,
    SacTypeFilter,
    RepliersFilter,
    GlobalFilterSummary,
  },
  mixins: [
    sacFilterModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appliedFilter: {},
      tempFilter: {},
      channels: [],
      changeOnPageFilter: false,
    };
  },
  computed: {
    filterLabel() {
      return this.$t('components.filter.ModalFilters.label.sacStatus.title');
    },
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter)) || this.changeOnPageFilter;
    },
    filtersObj() {
      return [
        {
          component: 'SacTypeFilter',
          filterType: FILTER_NAMES.SAC_TYPE,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.SAC_TYPE),
          },
        },
        {
          component: 'StatusFilter',
          filterType: 'sac_last_status',
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacStatus.statusTransitionOld'),
          },
        },
        {
          component: 'StatusFilter',
          filterType: FILTER_NAMES.SAC_STATUS,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacStatus.statusTransitionCurrent'),
            clearable: this.displayClearable(FILTER_NAMES.SAC_STATUS),
          },
        },
        {
          component: 'RepliersFilter',
          filterType: FILTER_NAMES.SAC_REPLIER,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacFilter.repliers'),
            selectItems: this.repliersToSelectItem(this.activeDashboardFilters?.repliers),
            clearable: this.displayClearable(FILTER_NAMES.SAC_REPLIER),
          },
        },
      ];
    },
  },
  methods: {
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.SAC_STATUS });
    },
    changeFilterValue() {
      this.changeOnPageFilter = true;
    },
  },
};
</script>

<style scoped lang="scss">
  .filter-sac-status-modal {
    line-height: 20px;
    color: var(--ds-color-text-1);
    font-size: var(--ds-font-size-sm);

    &__description {
      font-weight: var(--ds-font-weight-regular);
    }

    &__contrast {
      font-weight: var(--ds-font-weight-bold);
    }
  }

  .align-items-center {
    align-items: center;
  }
</style>
